import React, { useEffect, useState } from "react";
import { Plus, Trash2, Save, Pencil, X } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";
import { PageTitle } from "./shared";

const CATEGORIES = ["MARKERS & TAGS", "SHOW & EXHIBIT", "TOOLS & GADGETS", "FUN FINDS"];
const FLAGS = [
  { key: "featured", label: "GOOD STUFF", cls: "bg-lemon/60 text-plum-deep" },
  { key: "new_arrival", label: "FRESH", cls: "bg-sky/25 text-sky-deep" },
  { key: "bestseller", label: "BEST SELLER", cls: "bg-blush text-magenta-deep" },
  { key: "on_sale", label: "BARGAIN", cls: "bg-orange/20 text-orange" },
];

const EMPTY = { title: "", description: "", category: CATEGORIES[0], images: [], variants: [{ title: "Default Title", price: 0, available: true }], supplier: "The Etchery & Co", featured: false, bestseller: false, new_arrival: false, on_sale: false, active: true };

const Editor = ({ initial, onSave, onClose }) => {
  const [p, setP] = useState({ ...EMPTY, ...initial });
  const setVar = (i, patch) => setP({ ...p, variants: p.variants.map((v, idx) => (idx === i ? { ...v, ...patch } : v)) });
  return (
    <div className="bg-warmwhite border-2 border-magenta/40 rounded-[1.5rem] p-6 mb-6 space-y-4" data-testid="supply-editor">
      <div className="flex items-center justify-between">
        <h3 className="font-serif-display uppercase text-plum-deep text-xl">{initial?.id ? "EDIT PRODUCT" : "NEW PRODUCT"}</h3>
        <button onClick={onClose} data-testid="supply-editor-close" aria-label="Close"><X className="w-5 h-5" /></button>
      </div>
      <div className="grid md:grid-cols-2 gap-4">
        <input value={p.title} onChange={(e) => setP({ ...p, title: e.target.value })} placeholder="Product title *" data-testid="supply-editor-title"
          className="rounded-full px-4 py-2.5 bg-cream border border-plum-deep/15 text-sm focus:outline-none focus:border-magenta" />
        <select value={p.category} onChange={(e) => setP({ ...p, category: e.target.value })} data-testid="supply-editor-category"
          className="rounded-full px-4 py-2.5 bg-cream border border-plum-deep/15 text-sm focus:outline-none">
          {CATEGORIES.map((c) => <option key={c}>{c}</option>)}
        </select>
      </div>
      <textarea value={p.description} onChange={(e) => setP({ ...p, description: e.target.value })} rows={3} placeholder="Description" data-testid="supply-editor-description"
        className="w-full rounded-2xl px-4 py-2.5 bg-cream border border-plum-deep/15 text-sm focus:outline-none resize-none" />
      <textarea value={(p.images || []).join("\n")} onChange={(e) => setP({ ...p, images: e.target.value.split("\n").map((s) => s.trim()).filter(Boolean) })} rows={2}
        placeholder="Image URLs — one per line" data-testid="supply-editor-images"
        className="w-full rounded-2xl px-4 py-2.5 bg-cream border border-plum-deep/15 text-xs focus:outline-none resize-none" />
      <div>
        <p className="text-[11px] uppercase tracking-[0.18em] text-plum-deep/60 font-semibold mb-2">VARIANTS & PRICES</p>
        {p.variants.map((v, i) => (
          <div key={v.variant_id || i} className="flex gap-2 mb-2">
            <input value={v.title} onChange={(e) => setVar(i, { title: e.target.value })} placeholder="Option name" data-testid={`supply-variant-title-${i}`}
              className="flex-1 rounded-full px-4 py-2 bg-cream border border-plum-deep/15 text-sm focus:outline-none" />
            <input type="number" step="0.01" value={v.price} onChange={(e) => setVar(i, { price: parseFloat(e.target.value) || 0 })} data-testid={`supply-variant-price-${i}`}
              className="w-24 rounded-full px-3 py-2 bg-cream border border-plum-deep/15 text-sm focus:outline-none" />
            <label className="flex items-center gap-1.5 text-xs">
              <input type="checkbox" checked={v.available !== false} onChange={(e) => setVar(i, { available: e.target.checked })} className="accent-forest" /> IN STOCK
            </label>
            <button onClick={() => setP({ ...p, variants: p.variants.filter((_, idx) => idx !== i) })} disabled={p.variants.length === 1} aria-label="Remove variant" className="text-plum-deep/40 hover:text-red-500 disabled:opacity-30"><Trash2 className="w-4 h-4" /></button>
          </div>
        ))}
        <button onClick={() => setP({ ...p, variants: [...p.variants, { title: "", price: 0, available: true }] })} data-testid="supply-add-variant" className="text-xs uppercase font-bold text-magenta">+ ADD VARIANT</button>
      </div>
      <button onClick={() => onSave(p)} data-testid="supply-editor-save"
        className="inline-flex items-center gap-2 rounded-full bg-magenta text-cream px-6 py-2.5 text-xs uppercase tracking-[0.14em] font-bold hover:bg-plum">
        <Save className="w-3.5 h-3.5" /> SAVE PRODUCT
      </button>
    </div>
  );
};

const SupplyManager = () => {
  const [products, setProducts] = useState(null);
  const [editing, setEditing] = useState(null); // null | {} | product

  useEffect(() => {
    api.get("/admin/supply/products").then((r) => setProducts(r.data)).catch(() => setProducts([]));
  }, []);

  const patch = async (id, body) => {
    try {
      const { data } = await api.patch(`/admin/supply/products/${id}`, body);
      setProducts((l) => l.map((x) => (x.id === id ? data : x)));
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const save = async (p) => {
    if (!p.title.trim()) { toast.error("Give the product a title"); return; }
    try {
      if (p.id) {
        await patch(p.id, p);
        toast.success("Product updated");
      } else {
        const { data } = await api.post("/admin/supply/products", p);
        setProducts((l) => [...l, data]);
        toast.success("Product added to the Supply Room");
      }
      setEditing(null);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const remove = async (p) => {
    if (!window.confirm(`Delete "${p.title}" from the Supply Room?`)) return;
    await api.delete(`/admin/supply/products/${p.id}`);
    setProducts((l) => l.filter((x) => x.id !== p.id));
    toast.success("Product deleted");
  };

  return (
    <div data-testid="console-supply">
      <PageTitle kicker="THE ETCHERY · SHOP" title="SUPPLY ROOM">
        <button onClick={() => setEditing({})} data-testid="supply-new-product"
          className="inline-flex items-center gap-2 rounded-full bg-plum text-cream px-5 py-2.5 text-xs uppercase tracking-[0.14em] font-semibold hover:bg-magenta">
          <Plus className="w-3.5 h-3.5" /> NEW PRODUCT
        </button>
      </PageTitle>

      {editing !== null && <Editor initial={editing.id ? editing : null} onSave={save} onClose={() => setEditing(null)} />}

      {products === null ? <p className="text-plum-deep/60">Loading shelf…</p> : (
        <div className="space-y-3">
          {products.map((p) => (
            <div key={p.id} className={`bg-warmwhite border border-plum-deep/10 rounded-[1.25rem] px-5 py-4 flex flex-wrap items-center gap-3 ${!p.active ? "opacity-50" : ""}`} data-testid={`supply-row-${p.handle || p.id}`}>
              {p.images?.[0] && <img src={p.images[0]} alt="" className="w-12 h-12 rounded-xl object-cover border border-plum-deep/10" />}
              <div className="flex-1 min-w-[180px]">
                <p className="font-semibold text-sm text-plum-deep">{p.title}</p>
                <p className="text-[11px] text-plum-deep/60">{p.category} · from ${p.price_from?.toFixed(2)} · {p.variants?.length} variant{p.variants?.length === 1 ? "" : "s"}</p>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {FLAGS.map((f) => (
                  <button key={f.key} onClick={() => patch(p.id, { [f.key]: !p[f.key] })} data-testid={`supply-flag-${f.key}-${p.handle || p.id}`}
                    className={`px-2.5 py-1 rounded-full text-[9px] uppercase font-bold tracking-[0.08em] transition-opacity ${p[f.key] ? f.cls : "bg-plum-deep/5 text-plum-deep/35"}`}>
                    {f.label}
                  </button>
                ))}
              </div>
              <button onClick={() => patch(p.id, { active: !p.active })} data-testid={`supply-active-${p.handle || p.id}`}
                className={`px-3 py-1.5 rounded-full text-[10px] uppercase font-bold ${p.active ? "bg-forest/15 text-forest" : "bg-plum-deep/10 text-plum-deep/50"}`}>
                {p.active ? "LIVE" : "HIDDEN"}
              </button>
              <button onClick={() => setEditing(p)} data-testid={`supply-edit-${p.handle || p.id}`} aria-label="Edit" className="p-2 rounded-full hover:bg-blush/50"><Pencil className="w-4 h-4" /></button>
              <button onClick={() => remove(p)} data-testid={`supply-delete-${p.handle || p.id}`} aria-label="Delete" className="p-2 rounded-full hover:bg-red-50 text-plum-deep/50 hover:text-red-500"><Trash2 className="w-4 h-4" /></button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default SupplyManager;
